import type { PaletteName } from '@/art/palette';

/**
 * Parametertabellen voor personages.
 *
 * Alle maten in meters, gemeten vanaf de grond. Dit is de enige bron voor
 * personage-proporties: de builder leest ze, de test toetst ertegen. Wijzig hier, nooit
 * verspreid in de builder.
 *
 * De proporties volgen de character sheet van Luca Vitale: zes-hoofden-verhouding met
 * bewust te grote handen en voeten, zodat de figuur op isometrische afstand leest.
 *
 * Naast maten staan hier de **identiteitshaken**: haardracht, hoofddeksel, gezichts-
 * beharing, jasstijl en kaakvorm. Op 200 px is dát wat twee mensen van elkaar
 * onderscheidt — niet de jaskleur. Een archetype dat alleen in kleur verschilt van een
 * ander is niet goedgekeurd (zie `docs/CHARACTERS.md`).
 */

export interface CharacterProportions {
  /** Kruinhoogte zonder haarmassa. */
  readonly crownHeight: number;
  /** Gewrichtshoogtes boven de grond — de rig-pivots komen hier exact te staan. */
  readonly ankleHeight: number;
  readonly kneeHeight: number;
  readonly hipHeight: number;
  readonly wristHeight: number;
  readonly elbowHeight: number;
  readonly shoulderHeight: number;
  readonly neckHeight: number;
  readonly chinHeight: number;

  /** Breedtes, links-rechts. */
  readonly shoulderWidth: number;
  readonly hipWidth: number;
  /** Smalste punt van de romp, tussen ribben en heup. Bepaalt de taille-insnoering. */
  readonly waistWidth: number;
  readonly torsoDepth: number;
  /** Extra diepte van de buik vóór de taille; 0 voor een slank lichaam. */
  readonly bellyDepth: number;
  readonly neckWidth: number;
  readonly legThickness: number;
  readonly armThickness: number;

  /** Hoofd. */
  readonly headWidth: number;
  readonly headDepth: number;
  /** Kaakbreedte als fractie van de hoofdbreedte (0.7 smal, 0.95 zwaar). */
  readonly jawWidth: number;
  /** Kaakvorm: 0 rond, 1 hoekig. Zie `FacetedLoftRing.squareness`. */
  readonly jawSquareness: number;
  /** Schaal van de neus (1 = sheet). */
  readonly noseSize: number;
  readonly hairThickness: number;

  /** Bewust te grote extremiteiten. */
  readonly handLength: number;
  readonly handWidth: number;
  readonly footLength: number;
  readonly footWidth: number;
}

export interface CharacterColors {
  readonly skin: PaletteName;
  readonly hair: PaletteName;
  readonly coat: PaletteName;
  readonly shirt: PaletteName;
  readonly trousers: PaletteName;
  readonly shoes: PaletteName;
  readonly accent: PaletteName;
  /** Hoofddeksel. Genegeerd bij `hat: 'none'`, maar altijd aanwezig zodat een spec compleet is. */
  readonly hat: PaletteName;
}

/**
 * Haardracht. Alle stijlen zijn links-rechts symmetrisch, want de symmetrietest is de
 * goedkoopste manier om spiegelfouten in de builder te vangen.
 */
export type HairStyle = 'slickBack' | 'crop' | 'curly' | 'balding' | 'bald';
export const HAIR_STYLES: readonly HairStyle[] = ['slickBack', 'crop', 'curly', 'balding', 'bald'];

/** Hoofddeksel. Het sterkste silhouetverschil dat er op 200 px bestaat. */
export type HatStyle = 'none' | 'fedora' | 'peakedCap' | 'flatCap' | 'watchCap';
export const HAT_STYLES: readonly HatStyle[] = ['none', 'fedora', 'peakedCap', 'flatCap', 'watchCap'];

export type FacialHair = 'none' | 'stubble' | 'moustache' | 'goatee' | 'fullBeard';
export const FACIAL_HAIR: readonly FacialHair[] = ['none', 'stubble', 'moustache', 'goatee', 'fullBeard'];

/**
 * Jasstijl. Bepaalt lengte, dikte, kraag en sluiting — de massa van het bovenlichaam.
 *
 * - `blouson`: kort leren jack met geribde tailleband en manchetten (Luca).
 * - `sportsCoat`: colbert tot op de heup met revers.
 * - `overcoat`: lange jas tot halverwege het dijbeen, brede revers (Barili).
 * - `peacoat`: dikke korte havenjas met hoge brede kraag.
 * - `tunic`: gesloten uniformjas met knopen, schouderstukken en koppel (politie).
 */
export type CoatStyle = 'blouson' | 'sportsCoat' | 'overcoat' | 'peacoat' | 'tunic';
export const COAT_STYLES: readonly CoatStyle[] = ['blouson', 'sportsCoat', 'overcoat', 'peacoat', 'tunic'];

export interface CharacterFeatures {
  readonly hair: HairStyle;
  readonly hat: HatStyle;
  readonly facialHair: FacialHair;
  readonly coatStyle: CoatStyle;
  /** Open jas met zichtbaar shirt, of dichtgeknoopt. Een `tunic` is altijd dicht. */
  readonly openCoat: boolean;
  /** Vierkante bril met schildpad-montuur — Luca's sterkste identiteitshaak op afstand. */
  readonly glasses: boolean;
  /** Gouden ketting met hanger in de open kraag. */
  readonly chain: boolean;
  /** Gouden horloge om de rechterpols. */
  readonly watch: boolean;
  /** Stropdas onder colbert of overjas. Zichtbaar in de V of tussen de revers. */
  readonly tie: boolean;
}

export interface CharacterSpec {
  readonly name: string;
  readonly proportions: CharacterProportions;
  readonly colors: CharacterColors;
  readonly features: CharacterFeatures;
  /**
   * Maximaal aantal driehoeken; de test bewaakt dit.
   *
   * Plafond 24000. Werkelijk gebruik ligt rond 5.000–9.000: faceted lofts voor romp,
   * schedel, haar en ledematen, een jas met voering, en gezichtsgeometrie. Zie
   * `docs/CHARACTERS.md` voor de budgetten.
   */
  readonly triBudget: number;
}

/** Hard plafond voor procedurele personages. `approval.ts` weigert specs daarboven. */
export const PROCEDURAL_TRI_BUDGET = 24000;

/**
 * Basisproporties. Varianten passen hier hoogte en breedte op aan.
 *
 * De maten met bronvermelding staan letterlijk op het maatvoeringsblad van de character
 * sheet; de rest is daaruit afgeleid met normale volwassen verhoudingen. Dit is een slanke,
 * realistisch geproportioneerde figuur — geen mascotte met een groot hoofd.
 */
export const BASE_PROPORTIONS: CharacterProportions = {
  /** Sheet: 1.78 m totaal. */
  crownHeight: 1.78,
  ankleHeight: 0.09,
  kneeHeight: 0.48,
  hipHeight: 0.98,
  wristHeight: 0.86,
  elbowHeight: 1.13,
  shoulderHeight: 1.44,
  neckHeight: 1.5,
  /** Sheet: hoofd 0.23 m — kruin min 0.235 komt op de kin uit. */
  chinHeight: 1.545,

  /** Sheet: 0.48 m schouderbreedte. */
  shoulderWidth: 0.48,
  hipWidth: 0.36,
  waistWidth: 0.3,
  torsoDepth: 0.24,
  bellyDepth: 0,
  neckWidth: 0.12,
  legThickness: 0.17,
  armThickness: 0.115,

  // Het hoofd is dieper dan breed — dat is wat een schedel van een kubus onderscheidt, en
  // op de isometrische camera zie je dat goed omdat je het bovenvlak meeneemt.
  headWidth: 0.17,
  headDepth: 0.205,
  jawWidth: 0.82,
  jawSquareness: 0.55,
  noseSize: 1,
  /** Hoeveel de haarmassa boven de kruin uitkomt. Op gameafstand moet die haarvorm lezen. */
  hairThickness: 0.03,

  handLength: 0.2,
  handWidth: 0.1,
  footLength: 0.3,
  footWidth: 0.11,
};

/**
 * Het geskinde HD-model van Luca, gebouwd in Blender en geëxporteerd met
 * `scripts/export_luca_rig_glb.py` uit de asset-repo. Zie `docs/CHARACTERS.md`.
 *
 * Het bestand staat in `public/`, dus het pad is relatief aan de site-root en gaat niet
 * door de bundler heen.
 */
export const LUCA_MODEL_URL = 'models/luca_vitale_hd.glb';

/**
 * Luca Vitale — de speler.
 *
 * Herkenbaar uit silhouet aan de getoerde haarmassa met kuif, de vierkante schildpad-bril,
 * de circle beard, en de open bruine leren blouson met uitstaande kraag over een bordeaux
 * shirt met gouden ketting.
 *
 * In het spel komt Luca uit het HD-model. Deze spec blijft leidend voor zijn proporties en
 * gewrichtshoogtes — de animator leest `hipHeight` hieruit — en levert de procedurele
 * terugval als het model niet laadt.
 */
export const LUCA: CharacterSpec = {
  name: 'Luca_Vitale',
  // Luca is deliberately a little more graphic than the crowd baseline: the sheet's
  // oversized head, square jaw and hands are what keep his face and silhouette readable
  // at the fixed isometric camera distance.
  proportions: {
    ...BASE_PROPORTIONS,
    headWidth: 0.19,
    headDepth: 0.215,
    jawWidth: 0.84,
    jawSquareness: 0.68,
    handLength: 0.21,
    handWidth: 0.105,
    footLength: 0.31,
    footWidth: 0.115,
  },
  colors: {
    skin: 'skin',
    hair: 'hairBrown',
    coat: 'tobaccoLeather',
    shirt: 'bordeaux',
    trousers: 'charcoalOlive',
    shoes: 'shoeBrown',
    accent: 'gold',
    hat: 'charcoal',
  },
  features: {
    hair: 'slickBack',
    hat: 'none',
    facialHair: 'goatee',
    coatStyle: 'blouson',
    openCoat: true,
    glasses: true,
    chain: true,
    watch: true,
    tie: false,
  },
  triBudget: PROCEDURAL_TRI_BUDGET,
};

/**
 * Barili-soldaat: donker, gedisciplineerd, hoed op. De vijand in missie 1.
 *
 * Zware kaak, snor, lange dichte overjas — een blok in het straatbeeld, anders dan Luca's
 * korte jack.
 */
export const RIVAL_SOLDIER: CharacterSpec = {
  name: 'Rival_Soldier',
  proportions: {
    ...BASE_PROPORTIONS,
    crownHeight: 1.82,
    shoulderWidth: 0.53,
    waistWidth: 0.33,
    hipWidth: 0.38,
    legThickness: 0.18,
    armThickness: 0.125,
    jawWidth: 0.9,
    jawSquareness: 0.85,
    noseSize: 1.1,
  },
  colors: {
    skin: 'skinShadow',
    hair: 'hairBlack',
    coat: 'rivalNavy',
    shirt: 'rivalGrey',
    trousers: 'slateGrey',
    shoes: 'charcoal',
    accent: 'rivalGrey',
    hat: 'charcoal',
  },
  features: {
    hair: 'crop',
    hat: 'fedora',
    facialHair: 'moustache',
    coatStyle: 'overcoat',
    openCoat: false,
    glasses: false,
    chain: false,
    watch: false,
    tie: true,
  },
  triBudget: PROCEDURAL_TRI_BUDGET,
};

/**
 * Loyale Vitale-soldaat: warmer, opener, geen hoed. De drie die je in missie 1 redt.
 *
 * Kort haar, stoppels, open colbert over een licht shirt: je ziet zijn gezicht en zijn
 * hals, en daarmee lees je hem als bondgenoot.
 */
export const LOYAL_SOLDIER: CharacterSpec = {
  name: 'Loyal_Soldier',
  proportions: {
    ...BASE_PROPORTIONS,
    crownHeight: 1.74,
    shoulderWidth: 0.5,
    waistWidth: 0.31,
    jawWidth: 0.84,
    jawSquareness: 0.5,
  },
  colors: {
    skin: 'skin',
    hair: 'hairBlack',
    coat: 'oxideMaroon',
    shirt: 'cream',
    trousers: 'charcoal',
    shoes: 'shoeBrown',
    accent: 'weatheredWood',
    hat: 'charcoal',
  },
  features: {
    hair: 'crop',
    hat: 'none',
    facialHair: 'stubble',
    coatStyle: 'sportsCoat',
    openCoat: true,
    glasses: false,
    chain: false,
    watch: false,
    tie: false,
  },
  triBudget: PROCEDURAL_TRI_BUDGET,
};
